(function () {
  "use strict";

  /**
   * DiscussionGuide Class
   * Handles the multi-step interactive discussion guide functionality
   */
  class DiscussionGuide {
    constructor(container = ".discussion-guide") {
      this.container = document.querySelector(container);
      if (!this.container) {
        console.warn("Discussion guide container not found");
        return;
      }

      this.currentStep = 1;
      this.totalSteps = 3;
      this.userSelections = {
        step1: null,
        step2: [],
        step3: [],
      };

      this.init();
    }

    /**
     * Initialize the discussion guide
     */
    init() {
      this.normalizeActionControls();
      this.bindEvents();
      this.updateNavigationButtons();
      console.log("init", "DiscussionGuide initialized");
    }

    /**
     * Replace Divi button links that have no destination with semantic buttons.
     * An empty href reloads the current page whenever a click handler is not
     * available, so these controls should not be links in the first place.
     */
    normalizeActionControls() {
      this.container
        .querySelectorAll('a.dg-button[href=""]')
        .forEach((link) => {
          const button = document.createElement("button");

          Array.from(link.attributes).forEach(({ name, value }) => {
            if (name !== "href") {
              button.setAttribute(name, value);
            }
          });

          button.type = "button";
          button.innerHTML = link.innerHTML;
          link.replaceWith(button);
        });
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Radio button selections (single-select)
      this.container.addEventListener("click", (e) => {
        if (
          e.target.classList.contains("dg-button") &&
          e.target.classList.contains("-radio")
        ) {
          e.preventDefault();
          this.handleRadioSelection(e.target);
        }
      });

      // Checkbox selections (multi-select)
      this.container.addEventListener("click", (e) => {
        if (
          e.target.classList.contains("dg-button") &&
          e.target.classList.contains("-checkbox")
        ) {
          e.preventDefault();
          e.stopPropagation(); // Prevent event bubbling
          console.log("Checkbox click event fired", e.target);
          this.handleCheckboxSelection(e.target);
        }
      });

      // Navigation buttons
      this.container.addEventListener("click", (e) => {
        if (
          e.target.classList.contains("dg-button") &&
          e.target.classList.contains("-back")
        ) {
          e.preventDefault();
          if (!e.target.disabled) {
            // If at step 1, scroll to top instead of going to previous step
            if (this.currentStep === 1) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              this.goToPreviousStep();
            }
          }
        }

        if (
          e.target.classList.contains("dg-button") &&
          e.target.classList.contains("-next")
        ) {
          e.preventDefault();
          if (!e.target.disabled) {
            this.goToNextStep();
          }
        }
      });

      // Print functionality
      this.container.addEventListener("click", (e) => {
        if (
          e.target.classList.contains("dg-button") &&
          e.target.classList.contains("-print")
        ) {
          e.preventDefault();
          this.printResults();
        }

        // Handle download PDF button
        if (
          e.target.classList.contains("download") &&
          e.target.classList.contains("pdf")
        ) {
          e.preventDefault();
          this.printResults();
        }
      });
    }

    /**
     * Handle radio button selection (single-select)
     * @param {HTMLElement} button - The clicked button
     */
    handleRadioSelection(button) {
      const stepContainer = button.closest(".dg-step-item");
      const stepNumber = this.getStepNumber(stepContainer);

      // Remove previous selection
      const previousSelected = stepContainer.querySelector(
        ".dg-button.-radio.-selected"
      );
      if (previousSelected) {
        previousSelected.classList.remove("-selected");
      }

      // Add selection to clicked button
      button.classList.add("-selected");

      // Store selection
      this.userSelections[`step${stepNumber}`] = button.textContent.trim();

      // Enable next button
      this.updateNavigationButtons();
    }

    /**
     * Handle checkbox selection (multi-select)
     * @param {HTMLElement} button - The clicked button
     */
    handleCheckboxSelection(button) {
      const stepContainer = button.closest(".dg-step-item");
      const stepNumber = this.getStepNumber(stepContainer);
      const stepKey = `step${stepNumber}`;
      const selectionText = button.textContent.trim();

      // Ensure we have an array for multi-select steps
      if (!Array.isArray(this.userSelections[stepKey])) {
        this.userSelections[stepKey] = [];
      }

      // Toggle selection
      this.toggleSelection(button, stepKey, selectionText, stepContainer);

      // Enable next button if at least one option is selected
      this.updateNavigationButtons();

      // Update special text elements based on userSelections
      this.updateSpecialTextElements();
    }

    /**
     * Toggle selection for checkbox buttons
     * @param {HTMLElement} button - The clicked button
     * @param {string} stepKey - The step key (e.g., 'step2')
     * @param {string} selectionText - The text content of the button
     * @param {HTMLElement} stepContainer - The step container element
     */
    toggleSelection(button, stepKey, selectionText, stepContainer) {
      const isSelected = button.classList.contains("-selected");
      const choicesContainer = stepContainer.querySelector(
        ".dg-step-item--choices.multi-select"
      );

      if (isSelected) {
        // Deselect the button and remove from selections
        button.classList.remove("-selected");
        this.removeFromSelections(stepKey, selectionText);
      } else {
        // Handle selection based on whether it's the "Other" option
        if (button.classList.contains("other")) {
          this.handleOtherSelection(stepKey, selectionText, choicesContainer);
        } else {
          this.handleNonOtherSelection(
            stepKey,
            selectionText,
            choicesContainer
          );
        }
        button.classList.add("-selected");
      }
    }

    /**
     * Remove selection from the selections array
     * @param {string} stepKey - The step key
     * @param {string} selectionText - The text to remove
     */
    removeFromSelections(stepKey, selectionText) {
      const index = this.userSelections[stepKey].indexOf(selectionText);
      if (index > -1) {
        this.userSelections[stepKey].splice(index, 1);
      }
    }

    /**
     * Handle selection of the "Other" option
     * @param {string} stepKey - The step key
     * @param {string} selectionText - The "Other" text
     * @param {HTMLElement} choicesContainer - The choices container
     */
    handleOtherSelection(stepKey, selectionText, choicesContainer) {
      if (choicesContainer) {
        // Deselect all other checkboxes
        choicesContainer
          .querySelectorAll(".dg-button.-checkbox.-selected")
          .forEach((checkbox) => checkbox.classList.remove("-selected"));
      }
      // Replace selections with only "Other"
      this.userSelections[stepKey] = [selectionText];
    }

    /**
     * Handle selection of non-"Other" options
     * @param {string} stepKey - The step key
     * @param {string} selectionText - The selection text
     * @param {HTMLElement} choicesContainer - The choices container
     */
    handleNonOtherSelection(stepKey, selectionText, choicesContainer) {
      if (choicesContainer) {
        // Deselect "Other" if it's selected
        const otherButton = choicesContainer.querySelector(
          ".dg-button.-checkbox.other"
        );
        if (otherButton?.classList.contains("-selected")) {
          otherButton.classList.remove("-selected");
          this.removeFromSelections(stepKey, "Other");
        }
      }
      // Add the new selection
      this.userSelections[stepKey].push(selectionText);
    }

    /**
     * Update special text elements based on userSelections data
     */
    updateSpecialTextElements() {
      // Handle "I'm currently on treatment" selection in step 1
      this.updateTreatmentTextElements();

      // Handle "Lowering my IGF-1 level" selection in step 2
      this.updateLoweringIGF1TextElement();

      // Update step text elements
      this.updateStepTextElements();
    }

    /**
     * Update step text elements (txt-step-1, txt-step-2, txt-step-3)
     */
    updateStepTextElements() {
      // Update main elements
      this.updateStepTextElementsForContainer("");

      // Update print section elements
      this.updateStepTextElementsForContainer(".discussion-guide--print ");
    }

    /**
     * Update step text elements for a specific container
     */
    updateStepTextElementsForContainer(containerPrefix) {
      // Update step 1 text
      const step1Element = document.querySelector(
        `${containerPrefix}.txt-step-1`
      );
      if (step1Element) {
        const step1Selection = this.userSelections.step1 || "";
        step1Element.textContent = step1Selection;
        step1Element.classList.add("u-display-block");
      }

      // Update step 2 text
      const step2Element = document.querySelector(
        `${containerPrefix}.txt-step-2`
      );
      if (step2Element) {
        const step2Selections = this.userSelections.step2 || [];
        const step2Text = step2Selections.join(", ");
        step2Element.textContent = step2Text;
        step2Element.classList.add("u-display-block");
      }

      // Update step 3 text
      const step3Element = document.querySelector(
        `${containerPrefix}.txt-step-3`
      );
      if (step3Element) {
        const step3Selections = this.userSelections.step3 || [];
        const step3Text = step3Selections.join(", ");
        step3Element.textContent = step3Text;
        step3Element.classList.add("u-display-block");
      }
    }

    /**
     * Update treatment-related text elements based on step 1 selection
     */
    updateTreatmentTextElements() {
      // Update main elements
      this.updateTreatmentElements(".txt-on-treatment", ".txt-off-treatment");

      // Update print section elements with multiple selector strategies
      this.updateTreatmentElements(
        ".discussion-guide--print .txt-on-treatment",
        ".discussion-guide--print .txt-off-treatment"
      );
      this.updateTreatmentElements(
        ".discussion-guide--print .et_pb_module.txt-on-treatment",
        ".discussion-guide--print .et_pb_module.txt-off-treatment"
      );

      // Ensure print section treatment elements are properly handled
      this.updatePrintSectionTreatmentElements();
    }

    /**
     * Specifically handle treatment elements in the print section
     */
    updatePrintSectionTreatmentElements() {
      const printSection = document.querySelector(".discussion-guide--print");
      if (!printSection) {
        console.log("Print section not found");
        return;
      }

      // Check if "I'm currently on treatment" is selected in step 1
      const step1Selection = this.userSelections.step1 || "";
      const isOnTreatment =
        step1Selection.toLowerCase().includes("currently on treatment") &&
        !step1Selection.toLowerCase().includes("not currently") &&
        !step1Selection.toLowerCase().includes("never been");

      console.log("Print section treatment check:", {
        step1Selection: step1Selection,
        isOnTreatment: isOnTreatment,
        printSection: printSection,
      });

      // Find all treatment-related elements within the print section
      const printOnTreatmentElements = printSection.querySelectorAll(
        ".txt-on-treatment, .et_pb_module.txt-on-treatment"
      );
      const printOffTreatmentElements = printSection.querySelectorAll(
        ".txt-off-treatment, .et_pb_module.txt-off-treatment"
      );

      console.log("Print section elements found:", {
        onTreatmentCount: printOnTreatmentElements.length,
        offTreatmentCount: printOffTreatmentElements.length,
      });

      // Handle on-treatment elements - show only if isOnTreatment is true
      printOnTreatmentElements.forEach((element, index) => {
        console.log(
          `Processing print on-treatment element ${index + 1}:`,
          element
        );
        if (isOnTreatment) {
          element.style.display = "block";
          console.log(`Showing print on-treatment element ${index + 1}`);
        } else {
          element.style.display = "none";
          console.log(`Hiding print on-treatment element ${index + 1}`);
        }
      });

      // Handle off-treatment elements - show only if isOnTreatment is false
      printOffTreatmentElements.forEach((element, index) => {
        console.log(
          `Processing print off-treatment element ${index + 1}:`,
          element
        );
        if (isOnTreatment) {
          element.style.display = "none";
          console.log(`Hiding print off-treatment element ${index + 1}`);
        } else {
          element.style.display = "block";
          console.log(`Showing print off-treatment element ${index + 1}`);
        }
      });
    }

    /**
     * Update treatment elements for a specific container
     */
    updateTreatmentElements(onTreatmentSelector, offTreatmentSelector) {
      const onTreatmentElements =
        document.querySelectorAll(onTreatmentSelector);
      const offTreatmentElements =
        document.querySelectorAll(offTreatmentSelector);

      // Check if "I'm currently on treatment" is selected in step 1
      const step1Selection = this.userSelections.step1 || "";

      // More robust matching - check for various forms of "currently on treatment"
      const isOnTreatment =
        step1Selection.toLowerCase().includes("currently on treatment") &&
        !step1Selection.toLowerCase().includes("not currently") &&
        !step1Selection.toLowerCase().includes("never been");

      console.log("Treatment check:", {
        step1Selection: step1Selection,
        isOnTreatment: isOnTreatment,
        onTreatmentElementsCount: onTreatmentElements.length,
        offTreatmentElementsCount: offTreatmentElements.length,
        onTreatmentSelector: onTreatmentSelector,
        offTreatmentSelector: offTreatmentSelector,
        containsCurrently: step1Selection
          .toLowerCase()
          .includes("currently on treatment"),
        containsNot: step1Selection.toLowerCase().includes("not currently"),
        containsNever: step1Selection.toLowerCase().includes("never been"),
      });

      // Show/hide all instances of on-treatment elements
      if (onTreatmentElements.length > 0) {
        onTreatmentElements.forEach((element, index) => {
        //   console.log(`Processing on-treatment element ${index + 1}:`, element);
          if (isOnTreatment) {
            element.classList.add("u-display-block");
            // console.log(`Showing on-treatment element ${index + 1}`);
          } else {
            element.classList.remove("u-display-block");
            // console.log(`Hiding on-treatment element ${index + 1}`);
          }
        });
      } else {
        // console.log(
        //   "No on-treatment elements found with selector:",
        //   onTreatmentSelector
        // );
      }

      // Show/hide all instances of off-treatment elements
      if (offTreatmentElements.length > 0) {
        offTreatmentElements.forEach((element, index) => {
        //   console.log(
        //     `Processing off-treatment element ${index + 1}:`,
        //     element
        //   );
          if (isOnTreatment) {
            element.classList.remove("u-display-block");
         //   console.log(`Hiding off-treatment element ${index + 1}`);
          } else {
            element.classList.add("u-display-block");
          //  console.log(`Showing off-treatment element ${index + 1}`);
          }
        });
      } else {
        // console.log(
        //   "No off-treatment elements found with selector:",
        //   offTreatmentSelector
        // );
      }
    }

    /**
     * Update "Lowering my IGF-1 level" text element based on step 2 selection
     */
    updateLoweringIGF1TextElement() {
      // Update main element
      this.updateLoweringIGF1Element(".txt-lowering-igf1-level");

      // Update print section element
      this.updateLoweringIGF1Element(
        ".discussion-guide--print .txt-lowering-igf1-level"
      );
    }

    /**
     * Update IGF-1 level element for a specific container
     */
    updateLoweringIGF1Element(selector) {
      const txtElement = document.querySelector(selector);
      if (txtElement) {
        // Check if "Lowering my IGF-1 level" is selected in step 2
        const step2Selections = this.userSelections.step2 || [];
        const hasLoweringIGF1 = step2Selections.some(
          (selection) => selection.toLowerCase().includes("lowering my igf") // special characters "-" not working on .includes
        );

        if (hasLoweringIGF1) {
          txtElement.classList.add("u-display-block");
        } else {
          txtElement.classList.remove("u-display-block");
        }
      }
    }

    /**
     * Get step number from step container
     * @param {HTMLElement} stepContainer - The step container element
     * @returns {number} - The step number
     */
    getStepNumber(stepContainer) {
      // Find the step number by looking at the step container's position
      const allSteps = this.container.querySelectorAll(".dg-step-item");
      for (let i = 0; i < allSteps.length; i++) {
        if (allSteps[i] === stepContainer) {
          return i + 1;
        }
      }
      return 1;
    }

    /**
     * Go to the next step
     */
    goToNextStep() {
      // Save current step data before moving forward
      this.saveStepData(this.currentStep);
      
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
        this.showStep(this.currentStep);
        this.updateNavigationButtons();
      } else {
        this.showResults();
      }
    }

    /**
     * Go to the previous step
     */
    goToPreviousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.showStep(this.currentStep);
        this.updateNavigationButtons();

        // Scroll to the container for all previous steps
        const y = this.container.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }

    /**
     * Show specific step
     * @param {number} stepNumber - The step number to show
     */
    showStep(stepNumber) {
      // Hide all steps
      const allSteps = this.container.querySelectorAll(".dg-step-item");
      allSteps.forEach((step) => {
        step.classList.remove("show");
      });

      // Show current step - find the dg-step-item that corresponds to the step number
      const allStepItems = this.container.querySelectorAll(".dg-step-item");
      if (allStepItems[stepNumber - 1]) {
        allStepItems[stepNumber - 1].classList.add("show");
        const y = this.container.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }

    /**
     * Show results section
     */
    showResults() {
      // Submit final data to database
      this.submitFinalData();
      
      // Hide all steps
      const y = this.container.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: y, behavior: "smooth" });
      const allSteps = this.container.querySelectorAll(".dg-step-item");
      allSteps.forEach((step) => {
        step.classList.remove("show");
      });

      // Show all result sections
      const resultsSections = this.container.querySelectorAll(".dg-result");
      resultsSections.forEach((resultsSection) => {
        resultsSection.classList.add("show");
      });

      // Populate results in all sections
      this.populateResults();
    }

    /**
     * Update navigation buttons state
     */
    updateNavigationButtons() {
      const allStepItems = this.container.querySelectorAll(".dg-step-item");
      const currentStepElement = allStepItems[this.currentStep - 1];
      if (!currentStepElement) return;

      const nextButton = currentStepElement.querySelector(".dg-button.-next");
      const backButton = currentStepElement.querySelector(".dg-button.-back");

      if (nextButton) {
        const canProceed = this.canProceedToNext();
        nextButton.disabled = !canProceed;
      }

      if (backButton) {
        // Back button is always enabled since it scrolls to top when at step 1
        backButton.classList.remove("disabled");
      }
    }

    /**
     * Check if user can proceed to next step
     * @returns {boolean} - Whether user can proceed
     */
    canProceedToNext() {
      switch (this.currentStep) {
        case 1:
          return this.userSelections.step1 !== null;
        case 2:
          return this.userSelections.step2.length > 0;
        case 3:
          return this.userSelections.step3.length > 0;
        default:
          return false;
      }
    }

    /**
     * Populate results section with user selections
     */
    populateResults() {
   //   console.log("Populating results with selections:", this.userSelections);

      // Update step 1 result (only populate if elements exist)
      const step1Results = this.container.querySelectorAll(".txt-step-1");
   //   console.log("Step 1 result elements:", step1Results);
      step1Results.forEach((step1Result) => {
        if (step1Result && this.userSelections.step1) {
          step1Result.textContent = this.userSelections.step1;
     //     console.log("Updated step 1 with:", this.userSelections.step1);
        }
      });

      // Update step 2 result (only populate if elements exist)
      const step2Results = this.container.querySelectorAll(".txt-step-2");
   //  console.log("Step 2 result elements:", step2Results);
      step2Results.forEach((step2Result) => {
        if (step2Result && this.userSelections.step2.length > 0) {
          // Clear existing content
          step2Result.innerHTML = "";
          
          // Create elements safely to prevent XSS
          this.userSelections.step2.forEach((goal) => {
            const p = document.createElement("p");
            p.className = "checkbox";
            p.style.textAlign = "left";
            p.textContent = goal; // Use textContent to prevent XSS
            step2Result.appendChild(p);
          });
        //  console.log("Updated step 2 with:", this.userSelections.step2);
        }
      });

      // Update step 3 result (only populate if elements exist)
      const step3Results = this.container.querySelectorAll(".txt-step-3");
    //  console.log("Step 3 result elements:", step3Results);
      step3Results.forEach((step3Result) => {
        if (step3Result && this.userSelections.step3.length > 0) {
          // Clear existing content
          step3Result.innerHTML = "";
          
          // Create elements safely to prevent XSS
          this.userSelections.step3.forEach((symptom) => {
            const p = document.createElement("p");
            p.className = "checkbox";
            p.style.textAlign = "left";
            p.textContent = symptom; // Use textContent to prevent XSS
            step3Result.appendChild(p);
          });
      //    console.log("Updated step 3 with:", this.userSelections.step3);
        }
      });

    //   console.log(
    //     "Results populated. Some sections may not have text elements to populate."
    //   );
    }

    /**
     * Print the results
     */
    printResults() {
      console.log("Printing results...");
      
      // Track PDF print in database
      this.trackPdfPrint();

      // Look for print-friendly version
      const printSection = document.querySelector(".discussion-guide--print");
      if (printSection) {
        // console.log("Found print section, populating and printing...");

        // Populate print section with current results
        this.populatePrintSection(printSection);

        // Show print section and hide main guide
        printSection.style.display = "block";
        this.container.style.display = "none";

        // Trigger print
        window.print();

        // Restore visibility after print
        setTimeout(() => {
          printSection.style.display = "none";
          this.container.style.display = "block";
        }, 1000);
      } else {
        // console.log("No print section found, using fallback...");

        // Fallback: print all result sections
        const resultsSections = this.container.querySelectorAll(".dg-result");
        if (resultsSections.length > 0) {
          const printWindow = window.open("", "_blank");
          const allResultsHTML = Array.from(resultsSections)
            .map((section) => section.innerHTML)
            .join("");

          printWindow.document.write(`
                        <html>
                            <head>
                                <title>Discussion Guide Results</title>
                                <style>
                                    body { font-family: Arial, sans-serif; margin: 20px; }
                                    h2 { color: #541291; }
                                    h4 { color: #541291; border-bottom: 1px solid #541291; padding-bottom: 5px; }
                                    .result-step { margin-bottom: 20px; padding: 15px; border: 1px solid #ddd; border-radius: 5px; }
                                </style>
                            </head>
                            <body>
                                ${allResultsHTML}
                            </body>
                        </html>
                    `);
          printWindow.document.close();
          printWindow.print();
        }
      }
    }

    /**
     * Populate print section with results
     * @param {HTMLElement} printSection - The print section element
     */
    populatePrintSection(printSection) {
    //   console.log(
    //     "Populating print section with selections:",
    //     this.userSelections
    //   );

      // Update step 1 result in print section
      const step1Results = printSection.querySelectorAll(".txt-step-1");
      step1Results.forEach((step1Result) => {
        if (step1Result && this.userSelections.step1) {
          // Clear and create element safely to prevent XSS
          step1Result.innerHTML = "";
          const p = document.createElement("p");
          p.textContent = this.userSelections.step1; // Use textContent to prevent XSS
          step1Result.appendChild(p);
        }
      });

      // Update step 2 result in print section - use ul/li structure
      const step2Results = printSection.querySelectorAll(".txt-step-2");
      step2Results.forEach((step2Result) => {
        if (step2Result && this.userSelections.step2.length > 0) {
          // Clear existing content
          step2Result.innerHTML = "";

          // Create ul element
          const ulElement = document.createElement("ul");

          // Add each goal as a list item
          this.userSelections.step2.forEach((goal) => {
            const liElement = document.createElement("li");
            liElement.textContent = goal;
            liElement.style.textAlign = "left";
            ulElement.appendChild(liElement);
          });

          // Append ul to the step result element
          step2Result.appendChild(ulElement);

        //   console.log(
        //     "Updated print step 2 with ul/li structure:",
        //     this.userSelections.step2
        //   );
        }
      });

      // Update step 3 result in print section - use ul/li structure
      const step3Results = printSection.querySelectorAll(".txt-step-3");
      step3Results.forEach((step3Result) => {
        if (step3Result && this.userSelections.step3.length > 0) {
          // Clear existing content
          step3Result.innerHTML = "";

          // Create ul element
          const ulElement = document.createElement("ul");

          // Add each symptom as a list item
          this.userSelections.step3.forEach((symptom) => {
            const liElement = document.createElement("li");
            liElement.textContent = symptom;
            liElement.style.textAlign = "left";
            ulElement.appendChild(liElement);
          });

          // Append ul to the step result element
          step3Result.appendChild(ulElement);

        //   console.log(
        //     "Updated print step 3 with ul/li structure:",
        //     this.userSelections.step3
        //   );
        }
      });

    //   console.log("Print section populated successfully");
    }

    /**
     * Reset the discussion guide
     */
    reset() {
      this.currentStep = 1;
      this.userSelections = {
        step1: null,
        step2: [],
        step3: [],
      };

      // Clear all selections
      const allButtons = this.container.querySelectorAll(
        ".dg-button.-selected"
      );
      allButtons.forEach((button) => {
        button.classList.remove("-selected");
      });

      // Show first step
      this.showStep(1);
      this.updateNavigationButtons();

      // Hide all result sections
      const resultsSections = this.container.querySelectorAll(".dg-result");
      resultsSections.forEach((resultsSection) => {
        resultsSection.classList.remove("show");
      });
    }

    /**
     * Get current user selections
     * @returns {Object} - User selections object
     */
    getSelections() {
      return { ...this.userSelections };
    }

    /**
     * Set user selections (for testing or data restoration)
     * @param {Object} selections - Selections object
     */
    setSelections(selections) {
      this.userSelections = { ...selections };
      this.updateUIFromSelections();
    }

    /**
     * Update UI based on stored selections
     */
    updateUIFromSelections() {
      const allStepItems = this.container.querySelectorAll(".dg-step-item");

      // Update step 1 selections
      if (this.userSelections.step1 && allStepItems[0]) {
        const step1Container = allStepItems[0];
        const matchingButton = Array.from(
          step1Container.querySelectorAll(".dg-button.-radio")
        ).find((btn) => btn.textContent.trim() === this.userSelections.step1);
        if (matchingButton) {
          matchingButton.classList.add("-selected");
        }
      }

      // Update step 2 selections
      if (allStepItems[1]) {
        this.userSelections.step2.forEach((selection) => {
          const step2Container = allStepItems[1];
          const matchingButton = Array.from(
            step2Container.querySelectorAll(".dg-button.-checkbox")
          ).find((btn) => btn.textContent.trim() === selection);
          if (matchingButton) {
            matchingButton.classList.add("-selected");
          }
        });
      }

      // Update step 3 selections
      if (allStepItems[2]) {
        this.userSelections.step3.forEach((selection) => {
          const step3Container = allStepItems[2];
          const matchingButton = Array.from(
            step3Container.querySelectorAll(".dg-button.-checkbox")
          ).find((btn) => btn.textContent.trim() === selection);
          if (matchingButton) {
            matchingButton.classList.add("-selected");
          }
        });
      }
    }

    /**
     * Save step data to database via AJAX
     * @param {number} step - Current step number
     */
    saveStepData(step) {
      // Check if AJAX configuration exists
      if (typeof dgAjax === "undefined") {
        console.warn("Discussion Guide: AJAX configuration not found");
        return;
      }

      // Prepare data for current step
      const data = {
        action: "dg_save_step",
        discussion_guide_nonce: dgAjax.nonce,
        session_id: dgAjax.sessionId,
        current_step: step,
        treatment_status: this.userSelections.step1 || null,
        treatment_goals: this.userSelections.step2.length > 0 ? this.userSelections.step2 : null,
        symptoms_concerns: this.userSelections.step3.length > 0 ? this.userSelections.step3 : null,
      };

      // Send AJAX request
      if (window.jQuery) {
        jQuery.ajax({
          url: dgAjax.ajaxUrl,
          type: "POST",
          data: data,
          success: (response) => {
            if (dgAjax.debug) {
              console.log("Discussion Guide: Step " + step + " saved successfully", response);
            }
          },
          error: (xhr, status, error) => {
            console.error("Discussion Guide: Failed to save step " + step, error);
          },
        });
      } else {
        // Fallback to fetch API if jQuery not available
        fetch(dgAjax.ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(data).toString(),
        })
          .then((response) => response.json())
          .then((data) => {
            if (dgAjax.debug) {
              console.log("Discussion Guide: Step " + step + " saved successfully", data);
            }
          })
          .catch((error) => {
            console.error("Discussion Guide: Failed to save step " + step, error);
          });
      }
    }

    /**
     * Submit final data when all steps are complete
     */
    submitFinalData() {
      // Check if AJAX configuration exists
      if (typeof dgAjax === "undefined") {
        console.warn("Discussion Guide: AJAX configuration not found");
        return;
      }

      // Prepare complete submission data
      const data = {
        action: "dg_final_submit",
        discussion_guide_nonce: dgAjax.nonce,
        session_id: dgAjax.sessionId,
        current_step: 3,
        treatment_status: this.userSelections.step1 || null,
        treatment_goals: this.userSelections.step2.length > 0 ? this.userSelections.step2 : null,
        symptoms_concerns: this.userSelections.step3.length > 0 ? this.userSelections.step3 : null,
        completed: true,
      };

      // Send AJAX request
      if (window.jQuery) {
        jQuery.ajax({
          url: dgAjax.ajaxUrl,
          type: "POST",
          data: data,
          success: (response) => {
            if (dgAjax.debug) {
              console.log("Discussion Guide: Final submission saved successfully", response);
            }
          },
          error: (xhr, status, error) => {
            console.error("Discussion Guide: Failed to save final submission", error);
          },
        });
      } else {
        // Fallback to fetch API if jQuery not available
        fetch(dgAjax.ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(data).toString(),
        })
          .then((response) => response.json())
          .then((data) => {
            if (dgAjax.debug) {
              console.log("Discussion Guide: Final submission saved successfully", data);
            }
          })
          .catch((error) => {
            console.error("Discussion Guide: Failed to save final submission", error);
          });
      }
    }

    /**
     * Track PDF print/download
     */
    trackPdfPrint() {
      // Check if AJAX configuration exists
      if (typeof dgAjax === "undefined") {
        console.warn("Discussion Guide: AJAX configuration not found");
        return;
      }

      const data = {
        action: "dg_track_pdf",
        discussion_guide_nonce: dgAjax.nonce,
        session_id: dgAjax.sessionId,
      };

      // Send AJAX request
      if (window.jQuery) {
        jQuery.ajax({
          url: dgAjax.ajaxUrl,
          type: "POST",
          data: data,
          success: (response) => {
            if (dgAjax.debug) {
              console.log("Discussion Guide: PDF print tracked", response);
            }
          },
          error: (xhr, status, error) => {
            console.error("Discussion Guide: Failed to track PDF print", error);
          },
        });
      } else {
        // Fallback to fetch API if jQuery not available
        fetch(dgAjax.ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(data).toString(),
        })
          .then((response) => response.json())
          .then((data) => {
            if (dgAjax.debug) {
              console.log("Discussion Guide: PDF print tracked", data);
            }
          })
          .catch((error) => {
            console.error("Discussion Guide: Failed to track PDF print", error);
          });
      }
    }
  }

  // Auto-initialize function
  function initializeDiscussionGuide() {
    // console.log("Attempting to initialize Discussion Guide...");
    // console.log("Document ready state:", document.readyState);

    // Divi can expose et_fb / ET_Builder globals on ordinary front-end pages.
    // Use stable URL and document markers so the guide is skipped only when
    // the Visual Builder is actually active.
    const params = new URLSearchParams(window.location.search);
    const isDiviVisualBuilder =
      params.has("et_fb") ||
      params.has("et_bfb") ||
      window.location.href.includes("et-fb") ||
      document.body?.classList.contains("et-fb") ||
      document.body?.classList.contains("et_fb") ||
      document.documentElement.classList.contains("et-fb") ||
      document.documentElement.classList.contains("et_fb") ||
      window.top?.et_builder_utils_params?.builderType === "vb";

    if (isDiviVisualBuilder) {
      console.log(
        "Skipping Discussion Guide initialization in Divi Visual Builder"
      );
      return;
    }

    // Initialize discussion guide if container exists and not already initialized
    const container = document.querySelector(".discussion-guide");
    if (container && !window.discussionGuide) {
      window.discussionGuide = new DiscussionGuide();
    //   console.log("Discussion Guide initialized successfully");
    } else if (window.discussionGuide) {
    //   console.log("Discussion Guide already initialized");
    } else {
      //console.log("Discussion Guide container not found");
    }

    // Handle #get-started hash - redirect to #discussion-guide section
    if (window.location.hash === "#get-started") {
      const discussionGuideEl =
        document.getElementById("discussion-guide") ||
        document.querySelector(".discussion-guide");
      if (discussionGuideEl) {
        // Small delay to ensure page layout is settled
        setTimeout(() => {
          const y =
            discussionGuideEl.getBoundingClientRect().top +
            window.pageYOffset -
            32;
          window.scrollTo({ top: y, behavior: "smooth" });
          // Update hash silently to reflect actual position
          history.replaceState(null, null, "#discussion-guide");
        }, 100);
      }
    }
  }

  // Initialize immediately if DOM is already ready, otherwise wait for DOMContentLoaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeDiscussionGuide);
  } else {
    // DOM is already ready
    initializeDiscussionGuide();
  }

  // Export for manual initialization if needed
  window.DiscussionGuide = DiscussionGuide;

  // Export initialization function for manual control
  window.initDiscussionGuide = initializeDiscussionGuide;
})();
